import React, { useState, useEffect, useRef } from 'react';
import { useScoreboard } from './context/ScoreboardContext';
import { useLanguage } from './context/LanguageContext';

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

const PenaltyEntry = ({ penalty, onRemove, isGameRunning, team, isLiveView = false }) => {
  const [playerNumber, setPlayerNumber] = useState(penalty.playerNumber || '');
  const [isEditingTime, setIsEditingTime] = useState(false);
  const { editPenaltyTime, setPenaltyPlayerNumber } = useScoreboard();

  const handlePlayerNumberChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 99)) {
      setPlayerNumber(value);
      setPenaltyPlayerNumber(team, penalty.id, value);
    }
  };

  const handlePlayerNumberBlur = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 99)) {
      setPlayerNumber(value);
      setPenaltyPlayerNumber(team, penalty.id, value);
    } else {
      setPlayerNumber(penalty.playerNumber || '');
    }
  };

  const handleTimeClick = () => {
    if (!isGameRunning && !isLiveView) {
      setIsEditingTime(true);
    }
  };

  const handleTimeChange = (e) => {
    const value = e.target.value;
    const [minutes, seconds] = value.split(':').map(Number);
    if (!isNaN(minutes) && !isNaN(seconds) && minutes >= 0 && seconds >= 0 && seconds < 60) {
      const newSeconds = minutes * 60 + seconds;
      // Route the edit through the store, which encapsulates the penalty↔inferiority coupling
      // (front-segment edit before/after start — FR-035).
      editPenaltyTime(team, penalty.id, newSeconds);
    }
  };

  const handleTimeBlur = () => {
    setIsEditingTime(false);
  };

  const handleTimeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Keep local state in sync with penalty object
  useEffect(() => {
    setPlayerNumber(penalty.playerNumber || '');
  }, [penalty.playerNumber]);

  return (
    <div className="flex items-center justify-center gap-[1vh]">
      {isLiveView ? (
        // LiveView Layout - Spielernummer und Zeit nebeneinander, rechts ausgerichtet
        <div className="flex items-center justify-end w-full leading-tight whitespace-nowrap">
          <span className="text-[9vh] font-bold text-white font-chivo leading-none">
            {playerNumber ? playerNumber : ''}
          </span>
          <span className="text-[9vh] font-bold text-white font-chivo mx-[0.5vh] leading-none">
          {playerNumber ? '-' : ''}
          </span>
          <span className="text-[9vh] font-bold text-white font-chivo leading-none">
            {formatTime(penalty.timeInSeconds)}
          </span>
        </div>
      ) : (
        // Normale Ansicht mit Eingabefeldern
        <>
          <div className="w-[10vh] h-[8vh] p-[1vh] bg-white rounded-lg text-center relative border border-gray-300">
            <input
              type="number"
              min="1"
              max="99"
              value={playerNumber}
              onChange={handlePlayerNumberChange}
              onBlur={handlePlayerNumberBlur}
              disabled={isGameRunning}
              placeholder="99"
              className="w-full h-full text-[4vh] font-bold text-center bg-transparent appearance-textfield [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
          <div
            className="w-[16vh] h-[8vh] p-[1vh] bg-white rounded-lg border border-gray-300 flex items-center text-center cursor-pointer"
            onClick={handleTimeClick}
          >
            {isEditingTime ? (
              <input
                type="text"
                defaultValue={formatTime(penalty.timeInSeconds)}
                onChange={handleTimeChange}
                onBlur={handleTimeBlur}
                onKeyDown={handleTimeKeyDown}
                className="w-full h-full text-[4vh] font-bold text-center bg-transparent"
                autoFocus
                pattern="[0-9]{2}:[0-9]{2}"
                placeholder="00:00"
              />
            ) : (
              <span className="text-[4vh] font-bold">
                {formatTime(penalty.timeInSeconds)}
              </span>
            )}
          </div>
          {!isGameRunning && (
            <button
              onClick={() => onRemove(penalty.id)}
              className="w-[8vh] h-[8vh] bg-amber-500 text-black text-[4vh] font-bold rounded-lg hover:bg-amber-600"
              data-testid={`remove-penalty-${team}`}
            >
              -
            </button>
          )}
        </>
      )}
    </div>
  );
};

const PersonalPenalties = ({ team = 'home', isLiveView = false }) => {
  const { t } = useLanguage();
  const {
    isGameRunning,
    homePenalties,
    awayPenalties,
    addPenalty,
    removePenalty,
  } = useScoreboard();

  // Derived (store-backed) penalty projections: { id, playerNumber, timeInSeconds }.
  const penalties = team === 'home' ? homePenalties : awayPenalties;

  const handleAdd = () => {
    // Store enforces the max-3 cap; gate here too so the button hides at 3.
    if (penalties.length < 3) {
      addPenalty(team);
    }
  };

  const handleRemove = (id) => {
    removePenalty(team, id);
  };

  return (
    <div className={`flex flex-col ${isLiveView ? 'h-full' : ''}`}>
      <div className={`flex flex-col ${isLiveView ? 'gap-[0vh] items-end' : 'gap-[1vh] items-center'}`}>
        {penalties.map((penalty) => (
          <PenaltyEntry
            key={penalty.id}
            penalty={penalty}
            onRemove={handleRemove}
            isGameRunning={isGameRunning}
            team={team}
            isLiveView={isLiveView}
          />
        ))}
      </div>
      {!isLiveView && penalties.length < 3 && !isGameRunning && (
        <button
          onClick={handleAdd}
          className="mt-[2vh] px-[2vh] py-[1vh] bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-600 text-[2vh]"
        >
          {t('penalties.addPenalty')}
        </button>
      )}
    </div>
  );
};

export default PersonalPenalties;
